import { createContext, useContext, useMemo } from 'react';
import { getManagedComponents } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

/**
 * Every component of the form's workflow template, with this view template's overrides applied.
 * Unlike the list a form renders from, nothing is filtered out — so it still holds the components
 * that are rendered through a managing parent instead of as a field of their own (the start/end
 * DATE components a DATE_RANGE owns). Overrides are applied rather than left off because a view
 * template can be what makes one of those managed children required.
 *
 * Provided by {@link WorkflowComponentsProvider}; read it through {@link useManagedComponents}.
 * Null means no provider above, which is what lets a nested provider tell "nothing resolved yet"
 * apart from a workflow that genuinely has no components.
 */
export const WorkflowComponentsContext = createContext<API.AnyComponent[] | null>(null);

/**
 * The managed children a managing component owns — e.g. a DATE_RANGE's start and end DATE
 * components. They aren't in the list the form renders from, so they have to be looked up in the
 * workflow's full component set. Returns an empty array for components that manage nothing.
 */
export function useManagedComponents(component: API.AnyComponent): API.AnyComponent[] {
  const workflowComponents = useContext(WorkflowComponentsContext);

  return useMemo(
    () => getManagedComponents(component, workflowComponents ?? []),
    [component, workflowComponents],
  );
}
